import { useState, useEffect } from 'react';
import { Archive, RotateCcw, Trash2, X, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../api/client';

/**
 * Archive / restore / permanently delete a café — backed by
 * /internal/cafes/{id}/archive|restore|purge-preflight|purge
 * (backend app/services/cafe_lifecycle.py, which explains the staging).
 *
 *   active   -> "Archive café": instant, reversible. The café leaves every
 *               metric; licences and PCs are disabled.
 *   archived -> "Restore" or, superadmin only, "Delete permanently": the
 *               preflight lists every blocker (grace period, unpaid platform
 *               fee, holdbacks, live sessions) and the customer balances a
 *               purge would discard, which must be acknowledged by amount.
 *   purging  -> polled until it lands.
 *   purged   -> tombstone: when, and where the archive went.
 */

const parseUtc = (iso) => {
    if (!iso) return null;
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
    return new Date(hasTz ? iso : `${iso}Z`);
};
const fmtDate = (iso) => {
    const d = parseUtc(iso);
    return d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
};
const errText = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === 'string') return d;
    return d.message || fallback;
};

// The café page shows this only for a café that is not active (the banner);
// for an active one its ⋯ menu opens ArchiveModal directly, so a rarely used,
// destructive action is not a big red button beside Edit.
export default function CafeLifecycle({ cafe, canEdit, isSuperAdmin, onChanged }) {
    const status = cafe.status;
    const lc = cafe.lifecycle || {};
    const [modal, setModal] = useState(null); // 'archive' | 'purge' | null
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // A running purge moves on its own; poll until it settles.
    useEffect(() => {
        if (status !== 'purging') return undefined;
        const t = setInterval(() => onChanged?.(), 5000);
        return () => clearInterval(t);
    }, [status, onChanged]);

    const restore = async () => {
        if (!window.confirm(`Restore "${cafe.name}"? Its licences and PCs are re-enabled.`)) return;
        setBusy(true); setError(null);
        try {
            await api.post(`/internal/cafes/${cafe.id}/restore`);
            await onChanged?.();
        } catch (err) {
            setError(errText(err, 'Restore failed'));
        } finally { setBusy(false); }
    };

    if (status === 'active') {
        if (!canEdit) return null;
        return (
            <>
                <button type="button" className="btn btn--danger" onClick={() => setModal('archive')}>
                    <Archive size={16} /> Archive café
                </button>
                {modal === 'archive' && (
                    <ArchiveModal cafe={cafe} onClose={() => setModal(null)} onDone={onChanged} />
                )}
            </>
        );
    }

    return (
        <div className={`lifecycle-banner lifecycle-banner--${status}`}>
            <AlertTriangle size={18} />
            <div className="lifecycle-banner__text">
                {status === 'purged' ? (
                    <>
                        <strong>Deleted {fmtDate(lc.purged_at)}.</strong> The café&apos;s database was
                        dropped; this record is kept for invoices and the audit trail.
                        {lc.purge_backup_key && <div className="lifecycle-banner__mono">Archive: {lc.purge_backup_key}</div>}
                    </>
                ) : status === 'purging' ? (
                    <><strong>Deleting…</strong> Backing up and dropping the café&apos;s database. This page refreshes on its own.</>
                ) : (
                    <>
                        <strong>Archived {fmtDate(lc.archived_at)}</strong>
                        {lc.delete_reason ? ` — “${lc.delete_reason}”` : ''}. Out of all metrics; licences and PCs disabled.
                        Permanent deletion unlocks {fmtDate(lc.purge_allowed_after)}.
                        {lc.purge_error && <div className="lifecycle-banner__error">Last purge attempt failed: {lc.purge_error}</div>}
                    </>
                )}
                {error && <div className="lifecycle-banner__error">{error}</div>}
            </div>
            {(status === 'archived' || status === 'purge_failed') && canEdit && (
                <div className="lifecycle-banner__actions">
                    <button type="button" className="btn btn--secondary" onClick={restore} disabled={busy}>
                        <RotateCcw size={16} /> Restore
                    </button>
                    {isSuperAdmin && (
                        <button type="button" className="btn btn--danger" onClick={() => setModal('purge')} disabled={busy}>
                            <Trash2 size={16} /> Delete permanently…
                        </button>
                    )}
                </div>
            )}
            {status === 'purging' && lc.purge_error && isSuperAdmin && (
                <div className="lifecycle-banner__actions">
                    <button type="button" className="btn btn--danger" onClick={() => setModal('purge')}>
                        Resume deletion…
                    </button>
                </div>
            )}
            {modal === 'purge' && (
                <PurgeModal cafe={cafe} onClose={() => setModal(null)} onDone={onChanged} />
            )}
            <style>{`
        .lifecycle-banner {
          display: flex; align-items: flex-start; gap: var(--space-3);
          padding: var(--space-4) var(--space-5); margin-bottom: var(--space-8);
          border-radius: 14px; border: 1px solid rgba(244, 63, 94, 0.45);
          background: rgba(244, 63, 94, 0.10); color: var(--text-primary);
        }
        .lifecycle-banner--archived { border-color: rgba(255, 255, 255, 0.22); background: rgba(255, 255, 255, 0.06); }
        .lifecycle-banner--purging { border-color: rgba(234, 179, 8, 0.5); background: rgba(234, 179, 8, 0.10); }
        .lifecycle-banner > svg { flex-shrink: 0; margin-top: 2px; color: var(--text-secondary); }
        .lifecycle-banner__text { flex: 1; font-size: var(--text-sm); line-height: 1.5; color: var(--text-secondary); }
        .lifecycle-banner__text strong { color: var(--text-primary); }
        .lifecycle-banner__mono { font-family: var(--font-mono, monospace); font-size: var(--text-xs); margin-top: var(--space-2); word-break: break-all; }
        .lifecycle-banner__error { color: #f43f5e; margin-top: var(--space-2); }
        .lifecycle-banner__actions { display: flex; gap: var(--space-2); flex-wrap: wrap; }
      `}</style>
        </div>
    );
}

export function ArchiveModal({ cafe, onClose, onDone }) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const submit = async () => {
        setBusy(true); setError(null);
        try {
            await api.post(`/internal/cafes/${cafe.id}/archive`, { reason: reason.trim() || null });
            await onDone?.();
            onClose();
        } catch (err) {
            setError(errText(err, 'Archive failed'));
            setBusy(false);
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal__header">
                    <h2 className="modal__title">Archive {cafe.name}?</h2>
                    <button className="modal__close" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="modal__body">
                    <p className="modal-info">
                        The café leaves every dashboard and report immediately, its licences and PCs
                        are disabled, and its staff can no longer sign in. Nothing is deleted — you can
                        restore it at any time until it is permanently deleted, which is possible
                        30 days from now.
                    </p>
                    <label className="field">
                        <span className="field__label">Reason (kept in the audit log)</span>
                        <input
                            className="field__input"
                            value={reason}
                            maxLength={500}
                            placeholder="e.g. Not renewing — owner request"
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </label>
                    {error && <p className="modal-error">{error}</p>}
                </div>
                <div className="modal__footer">
                    <button className="btn btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="btn btn--danger" onClick={submit} disabled={busy}>
                        {busy ? <Loader2 size={16} className="spin" /> : <Archive size={16} />} Archive café
                    </button>
                </div>
            </div>
        </div>
    );
}

function PurgeModal({ cafe, onClose, onDone }) {
    const [pre, setPre] = useState(null);
    const [loading, setLoading] = useState(true);
    const [typed, setTyped] = useState('');
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // Bumped by "Re-check" to run the preflight again.
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let cancelled = false;
        api.get(`/internal/cafes/${cafe.id}/purge-preflight`)
            .then((res) => { if (!cancelled) { setPre(res.data); setError(null); } })
            .catch((err) => { if (!cancelled) setError(errText(err, 'Could not run the pre-delete checks')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [cafe.id, attempt]);

    const recheck = () => { setLoading(true); setError(null); setAttempt((n) => n + 1); };

    const money = pre?.money;
    const blockers = pre?.blockers || [];
    const canSubmit = pre && blockers.length === 0 && typed === cafe.name && (!pre.requires_ack || ack) && !busy;

    const submit = async () => {
        setBusy(true); setError(null);
        try {
            await api.post(`/internal/cafes/${cafe.id}/purge`, {
                confirm_name: typed,
                acknowledge: pre.requires_ack
                    ? { wallet_balance: money.wallet_balance, unexpired_minutes: money.unexpired_minutes }
                    : null,
            });
            await onDone?.();
            onClose();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === 'object' && detail.preflight) setPre(detail.preflight);
            setError(errText(err, 'Delete failed'));
            setBusy(false);
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal__header">
                    <h2 className="modal__title">Permanently delete {cafe.name}</h2>
                    <button className="modal__close" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="modal__body">
                    {loading ? (
                        <div className="empty-state"><Loader2 size={28} className="spin" /><p>Running checks…</p></div>
                    ) : !pre ? (
                        <p className="modal-error">{error}</p>
                    ) : (
                        <>
                            <p className="modal-info">
                                {pre.mode === 'tombstone_only'
                                    ? 'This café’s database no longer exists. Deleting records that and retires the café for good.'
                                    : 'The café’s database is backed up to private storage, then dropped. This cannot be undone from the portal.'}
                            </p>
                            {blockers.length > 0 && (
                                <div className="purge-blockers">
                                    <strong>Can&apos;t delete yet:</strong>
                                    <ul>{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
                                </div>
                            )}
                            {money && (
                                <table className="purge-money">
                                    <tbody>
                                        <tr><td>Platform fee owed to us</td><td>₹{money.platform_fee_due.toFixed(2)}</td></tr>
                                        <tr><td>Held back for the café</td><td>₹{money.holdbacks_due.toFixed(2)}</td></tr>
                                        <tr><td>Live PC sessions</td><td>{money.live_sessions}</td></tr>
                                        <tr><td>Customer wallets</td><td>₹{money.wallet_balance} ({money.customers_with_wallet} customers)</td></tr>
                                        <tr><td>Unexpired customer time</td><td>{money.unexpired_minutes} min ({money.customers_with_minutes} customers)</td></tr>
                                    </tbody>
                                </table>
                            )}
                            {blockers.length === 0 && pre.requires_ack && (
                                <label className="purge-ack">
                                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                                    <span>
                                        Customers lose ₹{money.wallet_balance} of wallet balance and {money.unexpired_minutes} minutes
                                        of time. I confirm this has been settled with them or the café.
                                    </span>
                                </label>
                            )}
                            {blockers.length === 0 && (
                                <label className="field">
                                    <span className="field__label">Type <strong>{cafe.name}</strong> to confirm</span>
                                    <input className="field__input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
                                </label>
                            )}
                            {error && <p className="modal-error">{error}</p>}
                        </>
                    )}
                </div>
                <div className="modal__footer">
                    <button className="btn btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
                    {pre && blockers.length > 0 ? (
                        <button className="btn btn--secondary" onClick={recheck} disabled={loading}>Re-check</button>
                    ) : (
                        <button className="btn btn--danger" onClick={submit} disabled={!canSubmit}>
                            {busy ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />} Delete permanently
                        </button>
                    )}
                </div>
                <style>{`
          .purge-blockers { padding: var(--space-3) var(--space-4); border-radius: 10px; background: rgba(244, 63, 94, 0.10); border: 1px solid rgba(244, 63, 94, 0.4); font-size: var(--text-sm); margin-bottom: var(--space-4); }
          .purge-blockers ul { margin: var(--space-2) 0 0 var(--space-5); list-style: disc; }
          .purge-money { width: 100%; font-size: var(--text-sm); margin-bottom: var(--space-4); border-collapse: collapse; }
          .purge-money td { padding: 6px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.08); color: var(--text-secondary); }
          .purge-money td:last-child { text-align: right; color: var(--text-primary); font-family: var(--font-mono, monospace); }
          .purge-ack { display: flex; gap: var(--space-3); align-items: flex-start; font-size: var(--text-sm); margin-bottom: var(--space-4); color: var(--text-secondary); }
          .purge-ack input { margin-top: 3px; }
        `}</style>
            </div>
        </div>
    );
}
